import { useEffect, useMemo, useState } from 'react';
import { buildSafetyScanProductPresentation } from '../../domain/acceptance/safetyScanProductPresentation';
import { parseOptionalCount } from '../../domain/acceptance/optionalCount';
import { resolveScanVerifyPendingCount } from '../../domain/safetyScanReport/scanVerificationWork';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import { resolveConfiguredCameraLabel, resolveConfiguredZoneLabel } from '../../domain/customerLabels/customerFacingControlLabel';
import { isEffectivenessClaimAllowedFromBoundary } from '../../domain/claimReadinessPresentation';
import type { SafetyScanReportContext } from '../../domain/acceptance/safetyScanReportContext';
import { reportsApi, type SafetyCaseRecord } from '../../services/reportsApi';
import { liveSafetyCaseDisplayTitle } from '../../domain/safetyCases/safetyScanWorkspaceHandoff';

export type ObservationFunnelQueueState = {
  progress: Record<string, unknown> | null;
  window: { start?: string | null; end?: string | null } | null;
};

export function useObservationFunnelQueue(reportId?: string | null): ObservationFunnelQueueState {
  const [state, setState] = useState<ObservationFunnelQueueState>({ progress: null, window: null });
  useEffect(() => {
    const key = String(reportId || '').trim();
    if (!key) {
      setState({ progress: null, window: null });
      return;
    }
    let cancelled = false;
    const loadQueue = reportsApi.getObservationDispositionQueue;
    if (typeof loadQueue !== 'function') {
      setState({ progress: null, window: null });
      return;
    }
    Promise.resolve(loadQueue(key, { limit: 1 }))
      .then((response) => {
        if (cancelled) return;
        const progress = response?.ok && response.progress && typeof response.progress === 'object'
          ? response.progress as Record<string, unknown>
          : null;
        const rawWindow = response?.window as { start?: string | null; end?: string | null; from?: string | null; to?: string | null } | null | undefined;
        const queueWindow = response?.ok && rawWindow
          ? {
              start: String(rawWindow.start || rawWindow.from || '').trim() || null,
              end: String(rawWindow.end || rawWindow.to || '').trim() || null,
            }
          : null;
        setState({ progress, window: queueWindow });
      })
      .catch(() => {
        if (!cancelled) setState({ progress: null, window: null });
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);
  return state;
}

export type FrozenScanReviewCounts = {
  pending: number | null;
  reviewed: number | null;
  confirmed: number | null;
  unresolved: number | null;
  unreviewed: number | null;
};

export function useOperatorLifecycleSnapshot(reportId?: string | null): {
  measurementReferences: Record<string, unknown> | null;
  review: FrozenScanReviewCounts | null;
} {
  const [state, setState] = useState<{
    measurementReferences: Record<string, unknown> | null;
    review: FrozenScanReviewCounts | null;
  }>({ measurementReferences: null, review: null });
  useEffect(() => {
    const key = String(reportId || '').trim();
    if (!key) {
      setState({ measurementReferences: null, review: null });
      return;
    }
    let cancelled = false;
    const load = reportsApi.getOperatorLifecycle;
    if (typeof load !== 'function') {
      setState({ measurementReferences: null, review: null });
      return;
    }
    Promise.resolve(load(key))
      .then((response) => {
        if (cancelled) return;
        const payload = response && typeof response === 'object' ? response as Record<string, unknown> : null;
        const refs = payload?.measurement_references;
        const reviewRaw = payload?.review && typeof payload.review === 'object'
          ? payload.review as Record<string, unknown>
          : null;
        setState({
          measurementReferences: refs && typeof refs === 'object' && !Array.isArray(refs)
            ? refs as Record<string, unknown>
            : null,
          review: reviewRaw ? {
            pending: parseOptionalCount(reviewRaw.pending),
            reviewed: parseOptionalCount(reviewRaw.reviewed),
            confirmed: parseOptionalCount(reviewRaw.confirmed),
            unresolved: parseOptionalCount(reviewRaw.unresolved),
            unreviewed: parseOptionalCount(reviewRaw.unreviewed ?? reviewRaw.pending),
          } : null,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ measurementReferences: null, review: null });
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);
  return state;
}

export function useMeasurementReferences(reportId?: string | null): Record<string, unknown> | null {
  return useOperatorLifecycleSnapshot(reportId).measurementReferences;
}

export function useObservationFunnelProgress(reportId?: string | null): Record<string, unknown> | null {
  return useObservationFunnelQueue(reportId).progress;
}

export function useSafetyScanProductStoryModel(args: {
  linkedCase?: SafetyCaseRecord | { title?: string | null; workflow_id?: string | null } | null;
  observationFunnelProgress?: Record<string, unknown> | null;
  finalizedFunnel?: Record<string, unknown> | null;
  workflowPrimaryAction?: {
    completed_at?: string | null;
    due_date?: string | null;
    title?: string | null;
    description?: string | null;
    assignee?: string | null;
    status?: string | null;
  } | null;
  patternLabel?: string | null;
  incidentType?: string | null;
  siteLabel?: string | null;
  zoneId?: string | null;
  areaLabel?: string | null;
  cameraId?: string | null;
  cameraLabel?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  timeZone?: string | null;
  signed?: boolean;
  workflowImpactState?: {
    baseline_exposure_count?: number | null;
    post_action_exposure_count?: number | null;
    measurement_status?: string | null;
    status?: string | null;
    percent_reduction?: number | null;
    baseline_exposure_rate?: number | null;
    post_action_exposure_rate?: number | null;
    measurement_trust?: {
      effectiveness_claim_allowed?: boolean | null;
      comparability?: Record<string, unknown> | null;
    } | null;
  } | null;
  canonicalClaimBoundary?: Parameters<typeof isEffectivenessClaimAllowedFromBoundary>[0];
  canSignSafetyScan?: boolean;
  hasCanonicalClaimReadiness?: boolean;
  reportReadyForSignOff?: boolean;
  claimReadinessControls?: Array<{
    control_id?: string | null;
    label?: string | null;
    status?: string | null;
    blocking?: boolean | null;
  }>;
  readinessBlockers?: Array<{
    key?: string | null;
    controlId?: string | null;
    label?: string | null;
    complete?: boolean;
    blocking?: boolean;
  }>;
  reviewComplete?: boolean;
  controlRecorded?: boolean;
  displayCustomerOwnerLabel?: string | null;
  observationDispositionPending?: boolean;
  pendingVerifyCountFromBlockers?: number | null;
  frozenReview?: FrozenScanReviewCounts | null;
  reportContext?: SafetyScanReportContext | null;
}) {
  return useMemo(() => {
    const frozenPending = parseOptionalCount(
      args.frozenReview?.unreviewed ?? args.frozenReview?.pending,
    );
    const pendingFromBlockers = parseOptionalCount(args.pendingVerifyCountFromBlockers);
    const pendingVerifyCount = resolveScanVerifyPendingCount({
      frozenUnreviewed: frozenPending,
      blockerPending: pendingFromBlockers,
    });
    const baseFunnel = args.observationFunnelProgress || args.finalizedFunnel || null;
    const funnelSource = frozenPending == null || !baseFunnel
      ? baseFunnel
      : {
          ...baseFunnel,
          unreviewed: frozenPending,
          pending_review_count: frozenPending,
          reviewed_count: parseOptionalCount(args.frozenReview?.reviewed)
            ?? Math.max(0, Number(baseFunnel.candidate_episode_count || 0) - frozenPending),
          confirmed_count: parseOptionalCount(args.frozenReview?.confirmed) ?? baseFunnel.confirmed_count,
        };
    const reportContext = args.reportContext || null;
    const implementedAt = String(
      reportContext?.control.implementedAt
      || args.workflowPrimaryAction?.completed_at
      || '',
    ).trim();
    const controlRecorded = reportContext
      ? reportContext.control.recorded
      : Boolean(args.controlRecorded);
    return buildSafetyScanProductPresentation({
      conditionLabel: args.patternLabel || args.incidentType,
      siteLabel: args.siteLabel,
      areaLabel: reportContext?.place.areaLabel
        || resolveConfiguredZoneLabel(args.zoneId)
        || args.areaLabel
        || args.zoneId,
      cameraLabel: reportContext?.place.cameraLabel
        || resolveConfiguredCameraLabel(args.cameraId)
        || args.cameraLabel,
      locationLine: reportContext?.place.locationLine,
      caseLabel: liveSafetyCaseDisplayTitle(args.linkedCase) || undefined,
      windowStart: reportContext?.timeWindow.start || args.windowStart,
      windowEnd: reportContext?.timeWindow.end || args.windowEnd,
      timeZone: args.timeZone,
      signed: Boolean(args.signed),
      technicalIds: [
        ...(reportContext?.place.technicalIds || []),
        args.zoneId,
        args.cameraId,
        String(args.linkedCase?.workflow_id || ''),
        reportContext?.reportId,
      ].filter((value, index, all) => Boolean(value) && all.indexOf(value) === index) as string[],
      funnelSource,
      pendingVerifyCount,
      verifyRequired: pendingVerifyCount != null && pendingVerifyCount > 0,
      patternSummary: args.patternLabel
        ? `Recurring ${String(args.patternLabel).toLowerCase()} exposure`
        : undefined,
      controlDescription: reportContext?.control.recorded
        ? (reportContext.control.description || undefined)
        : (String(args.workflowPrimaryAction?.title || args.workflowPrimaryAction?.description || '').trim() || undefined),
      controlOwner: reportContext?.control.ownerLabel
        || String(args.workflowPrimaryAction?.assignee || args.displayCustomerOwnerLabel || '').trim()
        || undefined,
      controlStatus: reportContext?.control.statusLabel
        || String(args.workflowPrimaryAction?.status || '').trim()
        || undefined,
      controlImplemented: implementedAt ? formatSiteDateTime(implementedAt, args.timeZone) : undefined,
      rawWorkflowBaselineCount: args.workflowImpactState?.baseline_exposure_count,
      rawWorkflowFollowupCount: args.workflowImpactState?.post_action_exposure_count,
      followUpStatus: String(args.workflowImpactState?.measurement_status || args.workflowImpactState?.status || '').trim() || undefined,
      comparison: {
        impact: args.workflowImpactState,
        claimAllowed: isEffectivenessClaimAllowedFromBoundary(args.canonicalClaimBoundary),
        comparison: {
          reviewGatePassed: !(pendingVerifyCount != null && pendingVerifyCount > 0),
        },
      },
      readiness: {
        readyToSign: Boolean(args.canSignSafetyScan),
        readinessUnknown: !args.hasCanonicalClaimReadiness && !args.reportReadyForSignOff && !(args.claimReadinessControls || []).length,
        reviewComplete: Boolean(args.reviewComplete) || pendingVerifyCount === 0,
        reviewReviewed: parseOptionalCount(args.frozenReview?.reviewed)
          ?? args.observationFunnelProgress?.reviewed_count as number | undefined,
        reviewCandidate: args.observationFunnelProgress?.candidate_episode_count as number | undefined,
        pendingVerifyCount,
        observationDispositionPending: pendingVerifyCount != null
          ? pendingVerifyCount > 0
          : Boolean(args.observationDispositionPending),
        controlRecorded,
        claimControls: args.claimReadinessControls,
        blockers: args.readinessBlockers,
      },
    });
  }, [args]);
}
